import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from "react-query";
import { message } from "antd";
import PaymentIntegrationService from "services/PaymentIntegrationService";
import { GET_PRODUCTS } from "redux/constants/PaymentIntegrations";
import unpackAndShowErrors from "helpers/djangoErrorUnapacker";
import PaymentService from "services/PaymentService";
import { TRANSACTIONS_KEY } from "./reporting";

const SQUARE_CONNECT_STATUS = "squareConnectStatus";
const GET_PRODUCT_PRICES = "getProductPrices";
const GET_SERVICES = "getServices";
const SERVICE_CATALOG = "serviceCatalog";
const PRODUCT_CATALOG = "productCatalog";
const GET_AEVA_PRODUCTS = "getAevaProducts";
const GET_AEVA_PRODUCTS_INVOICES = "useGetAevaProductsInvoices";
const GET_INVOICE_REFUND_DETAIL = "getInvoiceRefundDetail";
const CONTACT_PAYMENT_LINKS = "contactPaymentLinks";
const PAYMENT_LINK_DETAIL = "paymentLinkDetail";

const useGetSquareConnectStatus = () => {
  const { data: response, isLoading } = useQuery({
    queryKey: [SQUARE_CONNECT_STATUS],
    queryFn: () => PaymentIntegrationService.squareConnectionStatus(),
  });
  return { connected: response?.data?.connected || false, isLoading };
};

const useCreateCashPayment = (successMsg, schedulerOnSuccess) =>
  useMutation({
    mutationFn: (payload) =>
      PaymentIntegrationService.createCashPayment(payload),
    onSuccess: () => {
      message.success(successMsg, 3);
      schedulerOnSuccess && schedulerOnSuccess();
    },
  });

const useGetProducts = (payload) => {
  const { page, search } = payload;
  return useQuery({
    queryKey: [GET_PRODUCTS, page, search],
    queryFn: () => PaymentIntegrationService.getProducts(payload),
  });
};

const useGetProductPrices = (id) => {
  return useQuery({
    queryKey: [GET_PRODUCT_PRICES, id],
    queryFn: () => PaymentIntegrationService.listPrices(id),
  });
};

const useGetInifinteProductPrices = (id, ordering) =>
  useInfiniteQuery({
    queryKey: [GET_PRODUCT_PRICES, id],
    queryFn: ({ pageParam = 1 }) =>
      PaymentIntegrationService.listPrices(id, pageParam, ordering),
    select: (data) => ({
      pages: [].concat(
        ...(data?.pages?.map((page) => page?.data?.results) || [])
      ),
      pageParams: [...data.pageParams],
    }),
    getNextPageParam: (lastPage) => {
      if (lastPage?.data?.next) {
        return lastPage?.data?.current + 1;
      }
      return undefined;
    },
  });

const useCreateProductPrice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ data, product }) =>
      PaymentIntegrationService.createPrice(data, product),
    onSuccess: () => {
      queryClient.invalidateQueries(GET_PRODUCT_PRICES);
    },
    onError: ({ response }) => {
      unpackAndShowErrors(response?.data || {});
    },
  });
};

const useEditProductPrice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ data, product, priceId }) =>
      PaymentIntegrationService.editPrice(data, product, priceId),
    onSuccess: () => {
      queryClient.invalidateQueries(GET_PRODUCTS);
    },
    onError: ({ response }) => {
      unpackAndShowErrors(response?.data || {});
    },
  });
};

export const useDeletePrice = (onSuccess) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ productData, priceId }) =>
      PaymentIntegrationService.deletePrice(productData, priceId),
    onSuccess: () => {
      queryClient.invalidateQueries(GET_PRODUCT_PRICES);
      onSuccess?.();
    },
    onError: ({ response }) => {
      unpackAndShowErrors(response?.data || {});
    },
  });
};

export const useDeleteProduct = (onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ productData }) =>
      PaymentIntegrationService.deleteProduct(productData),
    onSuccess: () => {
      queryClient.invalidateQueries(GET_PRODUCTS);
      onSuccess?.();
    },
    onError: ({ response: { data } }) => {
      unpackAndShowErrors(data);
    },
  });
};

export const useCreateProduct = (onSuccessAction) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ data }) => PaymentIntegrationService.createProduct(data),
    onSuccess: () => {
      queryClient.invalidateQueries(GET_PRODUCTS);
      onSuccessAction?.();
    },
    onError: ({ response }) => {
      unpackAndShowErrors(response?.data || {});
    },
  });
};

export const useEditProduct = (onSuccessAction) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ data, productId }) =>
      PaymentIntegrationService.editProduct(data, productId),
    onSuccess: () => {
      queryClient.invalidateQueries(GET_PRODUCTS);
      onSuccessAction?.();
    },
    onError: ({ response }) => {
      unpackAndShowErrors(response?.data || {});
    },
  });
};

export const useGetServices = (payload) => {
  const { search } = payload;
  return useQuery({
    queryKey: [GET_SERVICES, search],
    queryFn: () => PaymentService.getServices(payload),
  });
};

export const useGetServiceCatalog = (payload = {}, enabled = true) => {
  const query = useQuery({
    queryKey: [SERVICE_CATALOG, payload],
    queryFn: () => PaymentService.getServiceCatalog(payload),
    enabled,
    keepPreviousData: true,
  });

  const data = query.data?.data;

  return {
    ...query,
    categories: data?.categories || [],
    services: data?.services || [],
    total: data?.count ?? 0,
  };
};

// The picker's product catalog. Deliberately not `useGetProducts`: that keys on
// [GET_PRODUCTS, page, search] only, so it would collide in cache with the
// paginated settings list, and its key is shared with the redux product screens.
//
// The existing endpoint already returns the shape the picker needs (products
// with their `invoiceprice_set`), so no new endpoint — but it IS paginated, and
// the rail is navigation: it has to be complete the moment the panel opens.
// So every page is drained rather than guessing at a page size big enough.
//
// `type: "product"` narrows the list to retail products: the checkout sells
// those, not the treatment-side items the standalone Products dropdown shows.
const PRODUCT_PAGE_SIZE = 200;

export const useGetProductCatalog = (payload = {}, enabled = true) => {
  const query = useInfiniteQuery({
    queryKey: [PRODUCT_CATALOG, payload],
    queryFn: ({ pageParam = 1 }) =>
      PaymentIntegrationService.getProducts({
        ...payload,
        type: "product",
        page: pageParam,
        per_page: PRODUCT_PAGE_SIZE,
      }),
    enabled,
    keepPreviousData: true,
    select: (data) => ({
      pages: [].concat(
        ...(data?.pages?.map((page) => page?.data?.results) || [])
      ),
      pageParams: [...data.pageParams],
    }),
    getNextPageParam: (lastPage) =>
      lastPage?.data?.next ? lastPage.data.current + 1 : undefined,
  });

  return { ...query, products: query.data?.pages || [] };
};

export const useSquareCheckout = (onSuccess, onError) => {
  return useMutation({
    mutationFn: (payload) => PaymentService.squareCheckout(payload),
    onSuccess: () => {
      onSuccess?.();
    },
    onError: ({ response: { data } }) => {
      unpackAndShowErrors(data);
      onError();
    },
  });
};

// New stepper-flow checkout — covers terminal, saved-card, and cash. The BE
// branches on the payload's `payment_type` to pick the right charge path.
export const useInvoiceCheckout = (onSuccess, onError) => {
  return useMutation({
    mutationFn: (payload) => PaymentService.invoiceCheckout(payload),
    onSuccess: () => {
      onSuccess?.();
    },
    onError: ({ response: { data } }) => {
      unpackAndShowErrors(data);
      onError?.();
    },
  });
};

export const useSendInvoiceLink = (onSuccess, onError) => {
  return useMutation({
    mutationFn: (payload) => PaymentService.sendInvoiceLink(payload),
    onSuccess: (response) => {
      onSuccess?.(response);
    },
    onError: (error) => {
      const errorBody = error?.response?.data;
      if (errorBody) unpackAndShowErrors(errorBody);
      onError?.(error);
    },
  });
};

export const useSendPaymentLinkNotification = (onSuccess, onError) => {
  return useMutation({
    mutationFn: (payload) =>
      PaymentService.sendPaymentLinkNotification(payload),
    onSuccess: (response, variables) => {
      onSuccess?.(response, variables);
    },
    onError: (error) => {
      const errorBody = error?.response?.data;
      if (errorBody) unpackAndShowErrors(errorBody);
      onError?.(error);
    },
  });
};

export const useGetContactPaymentLinks = (contactId, page, perPage) => {
  return useQuery({
    queryKey: [CONTACT_PAYMENT_LINKS, contactId, page, perPage],
    queryFn: () =>
      PaymentService.getPaymentLinks({
        contact_id: contactId,
        page,
        per_page: perPage,
      }),
    enabled: !!contactId,
    keepPreviousData: true,
    select: (response) => ({
      links: response?.data?.results || [],
      total: response?.data?.count || 0,
    }),
  });
};

export const useGetPaymentLinkDetails = (linkId) => {
  return useQuery({
    queryKey: [PAYMENT_LINK_DETAIL, linkId],
    queryFn: () => PaymentService.getPaymentLink(linkId),
    enabled: !!linkId,
    select: (response) => response?.data,
  });
};

export const useVoidPaymentLink = (onSuccess) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (linkId) => PaymentService.voidPaymentLink(linkId),
    onSuccess: (response) => {
      queryClient.invalidateQueries([CONTACT_PAYMENT_LINKS]);
      onSuccess?.(response);
    },
    onError: (error) => {
      const errorBody = error?.response?.data;
      if (errorBody) unpackAndShowErrors(errorBody);
    },
  });
};

export const useChargeAppointmentFee = (onSuccess) => {
  return useMutation({
    mutationFn: (payload) => PaymentService.chargeAppointmentFee(payload),
    onSuccess: () => {
      onSuccess?.();
    },
    onError: ({ response: { data } }) => {
      unpackAndShowErrors(data);
    },
  });
};

export const useChargeCancellationFee = (onSuccess) => {
  return useMutation({
    mutationFn: (payload) => PaymentService.chargeCancellationFee(payload),
    onSuccess: (data) => {
      onSuccess?.(data?.data);
    },
    onError: ({ response: { data } }) => {
      unpackAndShowErrors(data);
    },
  });
};

const useGetAevaProducts = () => {
  return useQuery({
    queryKey: [GET_AEVA_PRODUCTS],
    queryFn: () => PaymentService.getAevaProducts(),
  });
};

const useBuyAevaProduct = (onSuccess) => {
  return useMutation({
    mutationFn: (payload) => PaymentService.buyAevaProduct(payload),
    onSuccess: (data) => {
      onSuccess(data?.data);
    },
    onError: ({ response: { data } }) => {
      unpackAndShowErrors(data);
    },
  });
};

const useGetAevaProductsInvoices = (payload) => {
  return useQuery({
    queryKey: [GET_AEVA_PRODUCTS_INVOICES, payload],
    queryFn: () => PaymentService.getAevaProductInvoices(payload),
  });
};

const useGetInvoiceRefundDetail = (invoiceId) => {
  return useQuery({
    queryKey: [GET_INVOICE_REFUND_DETAIL, invoiceId],
    queryFn: () => PaymentService.getInvoiceRefundDetail(invoiceId),
    enabled: !!invoiceId,
  });
};

const useCreateInvoiceRefund = (onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ invoiceId, payload }) =>
      PaymentService.createInvoiceRefund(invoiceId, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries(TRANSACTIONS_KEY);
      onSuccess?.(data?.data);
    },
    onError: ({ response: { data } }) => {
      unpackAndShowErrors(data);
    },
  });
};

const useResendInvoiceReceipt = (onSuccess) => {
  return useMutation({
    mutationFn: (invoiceId) => PaymentService.resendInvoiceReceipt(invoiceId),
    onSuccess: (data) => {
      onSuccess?.(data?.data);
    },
    onError: ({ response }) => {
      unpackAndShowErrors(response?.data);
    },
  });
};

const useUpdateInvoiceItemEmployees = (onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, changes }) =>
      PaymentService.updateInvoiceItem(itemId, { changes }),
    onSuccess: (data) => {
      queryClient.invalidateQueries(TRANSACTIONS_KEY);
      onSuccess?.(data?.data);
    },
    onError: ({ response }) => {
      unpackAndShowErrors(response?.data);
    },
  });
};

export const useCreateStripePaymentIntent = () => {
  return useMutation({
    mutationFn: (payload) => PaymentService.createStripePaymentIntent(payload),
    onError: ({ response: { data } }) => {
      unpackAndShowErrors(data);
    },
  });
};

export {
  useGetSquareConnectStatus,
  useCreateCashPayment,
  useGetProducts,
  useGetProductPrices,
  useCreateProductPrice,
  useEditProductPrice,
  useGetInifinteProductPrices,
  useGetAevaProducts,
  useBuyAevaProduct,
  useGetAevaProductsInvoices,
  useGetInvoiceRefundDetail,
  useCreateInvoiceRefund,
  useResendInvoiceReceipt,
  useUpdateInvoiceItemEmployees,
};
